/* eslint-disable indent */
import {
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  GlobalOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  TrophyOutlined,
  TeamOutlined,
  AimOutlined,
  CloudOutlined,
  LinkOutlined
} from "@ant-design/icons";
import { Table, Button, Tag, Space, Tooltip, Popconfirm, Typography, theme, Badge, Grid } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState, useCallback } from "react";
import Latex from "react-latex-next";

import CreateEditAnnouncementModal from "./CreateEditAnnouncementModal";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";

const { Text } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

const THEME_DISPLAY_CONFIG = {
  INFO: "blue",
  SUCCESS: "green",
  WARNING: "orange",
  ERROR: "red",
  CRITICAL: "volcano"
};

const WebsiteAnnouncementsManagementDash = ({ userConfig, ROOT_URL }) => {
  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobileView = screens.md === false;
  const hasManagementAccess = userConfig?.websiteAnnouncements?.hasManagementAccess || false;
  const BASE_URL = "admin/website-announcement";

  const [announcementsList, setAnnouncementsList] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [conflictError, setConflictError] = useState(null);

  const fetchAnnouncements = useCallback(() => {
    setLoading(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/list`)
      .then((res) => {
        const list = res.data.announcementsList || [];
        setAnnouncementsList(list.map((item, idx) => ({ ...item, key: item.key, index: idx + 1 })));
      })
      .catch((err) => showErrorNotification(err))
      .finally(() => setLoading(false));
  }, [ROOT_URL, BASE_URL]);

  const handleSaveAnnouncement = (formValues) => {
    const isUpdate = !!initialValues?.key;
    const endpoint = isUpdate ? "update" : "create";

    const payload = {
      announcementParams: {
        ...formValues,
        ...(isUpdate && { id: initialValues.key })
      }
    };

    setConflictError(null);
    axios
      .post(`${ROOT_URL}/${BASE_URL}/${endpoint}`, payload)
      .then(() => {
        showSuccessNotification(`Announcement ${isUpdate ? "updated" : "created"} successfully!`);
        setIsModalOpen(false);
        fetchAnnouncements();
      })
      .catch((err) => {
        // Show priority collision errors inline inside the modal
        const status = err?.response?.status;
        const message = err?.response?.data?.message;
        if (status === 409 && message) {
          setConflictError(message);
        } else {
          showErrorNotification(err);
        }
      });
  };

  const handleDeleteAnnouncement = (id) => {
    axios
      .post(`${ROOT_URL}/${BASE_URL}/delete`, { id })
      .then(() => {
        showSuccessNotification("Announcement deleted successfully");
        fetchAnnouncements();
      })
      .catch((err) => showErrorNotification(err));
  };

  const columns = [
    // Dropped entirely on mobile — a permanently-visible serial number was called out
    // as unwanted screen space when reviewing announcement content on a phone.
    ...(isMobileView
      ? []
      : [
          {
            title: "S No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            align: "center",
            fixed: "left"
          }
        ]),
    {
      title: "Announcement Message",
      dataIndex: "message",
      key: "message",
      width: 260,
      render: (text) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            fontWeight: 500,
            color: token.colorText
          }}
        >
          <Latex>{text}</Latex>
        </div>
      )
    },
    {
      title: "Appearance",
      key: "config",
      width: 150,
      render: (row) => (
        <Space direction="vertical" size={0}>
          <Tag color={THEME_DISPLAY_CONFIG[row.theme] || "default"} style={{ margin: 0 }}>
            {row.theme}
          </Tag>
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {row.isDismissible ? "✕ User can dismiss" : "🔒 Persistent"}
          </Text>
        </Space>
      )
    },
    {
      title: (
        <Tooltip title="Higher priority = shown first. Route-targeted always beats global.">
          <span>
            <TrophyOutlined style={{ marginRight: 4 }} />
            Priority
          </span>
        </Tooltip>
      ),
      dataIndex: "priority",
      key: "priority",
      width: 90,
      align: "center",
      sorter: (a, b) => (b.priority ?? 0) - (a.priority ?? 0),
      render: (priority) => (
        <Badge
          count={priority ?? "—"}
          style={{
            backgroundColor:
              priority >= 500
                ? token.colorError
                : priority >= 200
                ? token.colorWarning
                : token.colorPrimary
          }}
          overflowCount={1000}
        />
      )
    },
    {
      title: (
        <Tooltip title="Empty = visible to all users. Non-empty = only users subscribed to one of these courses.">
          <span>
            <TeamOutlined style={{ marginRight: 4 }} />
            Audience
          </span>
        </Tooltip>
      ),
      key: "audience",
      width: 160,
      render: (row) => {
        const ids = row.allowedCourseIds || [];
        const names = row.allowedCourseNames || [];
        if (ids.length === 0) {
          return <Tag color="green">All Users</Tag>;
        }
        const items = names.length > 0 ? names : ids;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {items.map((item, index) => (
              <Tag key={index} color="purple" style={{ margin: 0 }}>
                {item}
              </Tag>
            ))}
          </div>
        );
      }
    },
    {
      title: (
        <Tooltip title="Targeting scope: global, per-subdomain, or specific page paths.">
          <span>
            <AimOutlined style={{ marginRight: 4 }} />
            Targeting Scope
          </span>
        </Tooltip>
      ),
      key: "targeting",
      width: 320,
      render: (row) => {
        const { subdomainTargeting = [], targeting = [] } = row;
        const SUBDOMAIN_LABELS = {
          main: "Main Site",
          dashboard: "Dashboard",
          profile: "Profile"
        };

        // SUBDOMAIN_TARGETED: has per-subdomain entries.
        if (subdomainTargeting.length > 0) {
          return (
            <Space direction="vertical" size={4}>
              {subdomainTargeting.map((entry, entryIdx) => {
                const subdomainLabel = SUBDOMAIN_LABELS[entry.subdomain] ?? entry.subdomain;

                if (entry.paths.length === 0) {
                  // Full subdomain — all pages.
                  return (
                    <Tag
                      key={entryIdx}
                      icon={<CloudOutlined />}
                      color="cyan"
                      style={{ fontSize: "12px" }}
                    >
                      {subdomainLabel} → All Pages
                    </Tag>
                  );
                }

                // Subdomain with specific path rules.
                return entry.paths.map((pathRule, pathIdx) => (
                  <Tooltip
                    key={`${entryIdx}-${pathIdx}`}
                    title={
                      pathRule.matchType === "SUBPAGES"
                        ? `${subdomainLabel}: matches ${pathRule.path} and all nested sub-pages`
                        : `${subdomainLabel}: matches ${pathRule.path} exactly`
                    }
                  >
                    <Tag
                      icon={
                        pathRule.matchType === "SUBPAGES" ? (
                          <FolderOpenOutlined />
                        ) : (
                          <FileTextOutlined />
                        )
                      }
                      color="geekblue"
                      style={{ borderRadius: "4px", fontSize: "12px", marginBottom: 2 }}
                    >
                      {subdomainLabel} → {pathRule.path}
                      {pathRule.matchType === "SUBPAGES" && "/*"}
                    </Tag>
                  </Tooltip>
                ));
              })}
            </Space>
          );
        }

        // PAGE_TARGETED: legacy targeting field, applies to all subdomains.
        if (targeting.length > 0) {
          return (
            <Space direction="vertical" size={4}>
              <Tag icon={<LinkOutlined />} color="purple" style={{ fontSize: "11px" }}>
                All Subdomains
              </Tag>
              <Space size={[0, 4]} wrap>
                {targeting.map((t, idx) => (
                  <Tooltip
                    key={idx}
                    title={
                      t.matchType === "SUBPAGES"
                        ? "Matches this page and all nested sub-pages (all subdomains)"
                        : "Matches this exact URL only (all subdomains)"
                    }
                  >
                    <Tag
                      icon={
                        t.matchType === "SUBPAGES" ? <FolderOpenOutlined /> : <FileTextOutlined />
                      }
                      color="geekblue"
                      style={{ borderRadius: "4px", fontSize: "12px" }}
                    >
                      {t.path}
                      {t.matchType === "SUBPAGES" && "/*"}
                    </Tag>
                  </Tooltip>
                ))}
              </Space>
            </Space>
          );
        }

        // GLOBAL: no restrictions at all.
        return (
          <Tag color="magenta" icon={<GlobalOutlined />}>
            Entire Website (Global)
          </Tag>
        );
      }
    },
    {
      title: "Timing & Status",
      key: "status_schedule",
      width: 200,
      render: (row) => {
        const isExpired = row.expiresAt && dayjs().isAfter(dayjs(row.expiresAt));
        return (
          <Space direction="vertical" size={2}>
            {isExpired ? (
              <Tag color="default">EXPIRED</Tag>
            ) : row.isActive ? (
              <Tag color="processing">ACTIVE</Tag>
            ) : (
              <Tag>DRAFT</Tag>
            )}
            <div style={{ fontSize: "11px", marginTop: "4px" }}>
              <Text type="secondary">In: </Text>
              {dayjs(row.startDate).format("DD/MM/YY HH:mm")}
              <br />
              <Text type="secondary">Out: </Text>
              {row.expiresAt ? (
                dayjs(row.expiresAt).format("DD/MM/YY HH:mm")
              ) : (
                <Text type="warning" italic>
                  Manual Stop
                </Text>
              )}
            </div>
          </Space>
        );
      }
    },
    {
      title: "Audit",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 130,
      render: (name) => (
        <div style={{ fontSize: "12px" }}>
          <Text strong>{name || "System"}</Text>
          <div style={{ fontSize: "10px", color: token.colorTextSecondary }}>Author</div>
        </div>
      )
    },
    {
      title: "Actions",
      key: "action",
      fixed: isMobileView ? undefined : "right",
      width: 100,
      align: "center",
      render: (row) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              type="text"
              shape="circle"
              icon={<EditOutlined style={{ color: token.colorPrimary }} />}
              disabled={!hasManagementAccess}
              onClick={() => {
                setInitialValues(row);
                setIsModalOpen(true);
              }}
            />
          </Tooltip>

          <Popconfirm
            title="Delete Announcement"
            description="Are you sure you want to delete this?"
            icon={<QuestionCircleOutlined style={{ color: token.colorError }} />}
            onConfirm={() => handleDeleteAnnouncement(row.key)}
            okText="Yes"
            cancelText="No"
            disabled={!hasManagementAccess}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                shape="circle"
                danger
                disabled={!hasManagementAccess}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  return (
    <div style={{ padding: clamp(12, 24), minHeight: "100vh" }}>
      <div
        style={{
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          background: token.colorBgContainer
        }}
      >
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px"
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Website Announcements</h2>
            <Text type="secondary">
              Manage the &quot;Butter Bar&quot; notifications across the platform. Multiple
              announcements can be active simultaneously — users see the highest-priority one first
              and can dismiss through the stack.
            </Text>
          </div>
          {hasManagementAccess && (
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setInitialValues(null);
                setIsModalOpen(true);
              }}
            >
              + New Announcement
            </Button>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={announcementsList}
          bordered
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 1500 }}
        />
      </div>

      {isModalOpen && (
        <CreateEditAnnouncementModal
          isModalOpen={isModalOpen}
          setIsModalOpen={(open) => {
            if (!open) {
              setConflictError(null);
            }
            setIsModalOpen(open);
          }}
          onSubmit={handleSaveAnnouncement}
          initialValues={initialValues}
          conflictError={conflictError}
          ROOT_URL={ROOT_URL}
        />
      )}
    </div>
  );
};

export default WebsiteAnnouncementsManagementDash;
